// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography'
import Icon from 'src/features/common/CustomIcon'
import Link from '@mui/material/Link';
// ** Store Imports
import { SelectOption } from 'src/types/common';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
// ** Custom Components Imports
import Tooltip from '@mui/material/Tooltip'
import { client } from 'src/utils/axios-client'
import { leadEndpoints } from 'src/configs/endpoints'

import _ from 'lodash'
import { ActivitySummaryDashboardResponse, ActivityType, CadenceListDialogStore, DashboardTodoStore, DashboardViewStore, InboundCallStore, KPISummaryDashboardResponse, Lead, LeadListStore, LeadScreenStore, QuickLeadDialogStore, ViewType } from '../../stores/LeadStore'
import { ActivityColor, colorDateTooltipOverdue, colorDateTooltipOverdueDashboard, nextDateDays, nextDateDaysDashboard } from '../../utils/date-utils'
import VoicemailTwoToneIcon from '@mui/icons-material/VoicemailTwoTone';
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';
import ArrowDownwardTwoToneIcon from '@mui/icons-material/ArrowDownwardTwoTone';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import TrendingDownTwoToneIcon from '@mui/icons-material/TrendingDownTwoTone';
import StandardSkeleton from '../common/StandardSkeleton';
import CardContent from '@mui/material/CardContent'
import Skeleton from '@mui/material/Skeleton'
import Avatar from '@mui/material/Avatar';
import { Solid } from 'mdi-material-ui';
import PerformanceCard from '../common/PerformanceCard';
import TrendingFlatTwoToneIcon from '@mui/icons-material/TrendingFlatTwoTone';
import RadialBarChart from '../common/RadialBarChart';
import RadialBarChartDashboard from '../common/RadialBarChartDashboard';


type Props = {
    title: string,
    userId: string
}


const DashboardKPIDetails = ({ title, userId }: Props) => {
    const records = KPISummaryDashboardResponse.useState(s => s.record);
    const selectedCustomer = KPISummaryDashboardResponse.useState(s => s.record.selectedCustomer);
    const [reloadRecords, setReloadRecords] = useState<boolean>(false)
    
    const [customerId, setCustomerId] = useState<string>('')

    let record = records.kpiDetails.find(x => x.customerId == selectedCustomer);

    useEffect(() => {
        record = records.kpiDetails.find(x => x.customerId == selectedCustomer);
    }, [records])
    
    const handleSelection = (activityType: string, activityTitle: string) => {
        
        DashboardViewStore.update(s => { s.activityDate = "thismonth" });
        DashboardViewStore.update(s => { s.activityType = activityType });
        DashboardViewStore.update(s => { s.customerId = selectedCustomer });
        DashboardViewStore.update(s => { s.activityHistoryTitle = activityTitle + " for This Month" });

        DashboardViewStore.update(s => { s.showTodoList = false });
        DashboardViewStore.update(s => { s.showActivityWorkList = false });
        DashboardViewStore.update(s => { s.showInboundCallList = false });
        DashboardViewStore.update(s => { s.showActivityHistoryList = true });
        DashboardViewStore.update(s => { s.showDefault = false });
        DashboardViewStore.update(s => { s.showDetail = true });
        DashboardViewStore.update(s => { s.showTaskList = false });
    };

    if (record) {
        return (
            <Grid container spacing={2} justifyContent={'center'}>
                <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column', width: '100%' }} >
                    <Card>
                        <Typography sx={{ fontSize: 16, color: 'DarkGrey', ml: 3, mr: 3, width: '100%' }}>
                            {title}
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card sx={{ pt: 4 , pb: 3}}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 30, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('activity', 'Activities')}
                                    >
                                        {record.activityCount}
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 16, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('activity', 'Activities')}
                                    >
                                        Activities
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Typography sx={{ fontSize: 18, color: 'CornflowerBlue' }}>
                                    Target KPI: {record.activityKPI}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <RadialBarChartDashboard subHeading="" seriesValue={record.activityProgressPercent} toolTip="KPI Progress" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difActivity == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'red' }} >
                                            {record.activityDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difActivity == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'green' }} >
                                            +{record.activityDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difActivity == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <Typography sx={{ fontSize: 12, color: 'orange' }} >
                                            Same as KPI
                                        </Typography>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difActivity == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <TrendingDownTwoToneIcon sx={{ color: 'red' }} />
                                    </Tooltip>
                                }
                                {record.difActivity == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <TrendingUpTwoToneIcon sx={{ color: 'green' }} />
                                    </Tooltip>
                                }
                                {record.difActivity == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <TrendingFlatTwoToneIcon sx={{ color: 'orange' }} />
                                    </Tooltip>
                                }
                            </Grid>
                            
                        </Grid>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card sx={{ pt: 4, pb: 3 }}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 30, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('mi', 'Meaningful Interactions')}
                                    >
                                        {record.miCount}
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 16, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('mi', 'Meaningful Interactions')}
                                    >
                                        Meaningful Interactions
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Typography sx={{ fontSize: 18, color: 'CornflowerBlue' }}>
                                    Target KPI: {record.mikpi}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <RadialBarChartDashboard subHeading="" seriesValue={record.miProgressPercent} toolTip="KPI Progress" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difMI == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'red' }} >
                                            {record.miDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difMI == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'green' }} >
                                            +{record.miDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difMI == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <Typography sx={{ fontSize: 12, color: 'orange' }} >
                                            Same as KPI
                                        </Typography>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difMI == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <TrendingDownTwoToneIcon sx={{ color: 'red' }} />
                                    </Tooltip>
                                }
                                {record.difMI == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <TrendingUpTwoToneIcon sx={{ color: 'green' }} />
                                    </Tooltip>
                                }
                                {record.difMI == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <TrendingFlatTwoToneIcon sx={{ color: 'orange' }} />
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card sx={{ pt: 4, pb: 3 }}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 30, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('mql', 'MQLs')}
                                    >
                                        {record.mqlCount}
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 16, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('mql', 'mql')}
                                    >
                                        MQLs
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Typography sx={{ fontSize: 18, color: 'CornflowerBlue' }}>
                                    Target KPI: {record.mqlkpi}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <RadialBarChartDashboard subHeading="" seriesValue={record.mqlProgressPercent} toolTip="KPI Progress" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difMQL == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'red' }} >
                                            {record.mqlDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difMQL == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'green' }} >
                                            +{record.mqlDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difMQL == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <Typography sx={{ fontSize: 12, color: 'orange' }} >
                                            Same as KPI
                                        </Typography>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difMQL == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <TrendingDownTwoToneIcon sx={{ color: 'red' }} />
                                    </Tooltip>
                                }
                                {record.difMQL == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <TrendingUpTwoToneIcon sx={{ color: 'green' }} />
                                    </Tooltip>
                                }
                                {record.difMQL == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <TrendingFlatTwoToneIcon sx={{ color: 'orange' }} />
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card sx={{ pt: 4, pb: 3 }}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column'}} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 30, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('dc', 'Discovery Calls')}
                                    >
                                        {record.dcCount}
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 16, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('dc', 'Discovery Calls')}
                                    >
                                        Discovery Calls
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Typography sx={{ fontSize: 18, color: 'CornflowerBlue' }}>
                                    Target KPI: {record.dckpi}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <RadialBarChartDashboard subHeading="" seriesValue={record.dcProgressPercent} toolTip="KPI Progress" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difDC == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'red' }} >
                                            {record.dcDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difDC == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'green' }} >
                                            +{record.dcDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difDC == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <Typography sx={{ fontSize: 12, color: 'orange' }} >
                                            Same as KPI
                                        </Typography>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difDC == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <TrendingDownTwoToneIcon sx={{ color: 'red' }} />
                                    </Tooltip>
                                }
                                {record.difDC == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <TrendingUpTwoToneIcon sx={{ color: 'green' }} />
                                    </Tooltip>
                                }
                                {record.difDC == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <TrendingFlatTwoToneIcon sx={{ color: 'orange' }} />
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card sx={{ pt: 4, pb: 3 }}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 30, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('opp', 'Opportunities')}
                                    >
                                        {record.oppCount}
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Tooltip title='Click to view activities'>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        sx={{ fontSize: 16, color: 'CornflowerBlue' }}
                                        onClick={() => handleSelection('opp', 'Opportunities')}
                                    >
                                        Opportunities
                                    </Link>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <Typography sx={{ fontSize: 18, color: 'CornflowerBlue' }}>
                                    Target KPI: {record.oppKPI}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                <RadialBarChartDashboard subHeading="" seriesValue={record.oppProgressPercent} toolTip="KPI Progress" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difOpp == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'red' }} >
                                            {record.oppDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difOpp == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <Typography sx={{ fontSize: 12, color: 'green' }} >
                                            +{record.oppDifCount} Compared to KPI
                                        </Typography>
                                    </Tooltip>
                                }
                                {record.difOpp == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <Typography sx={{ fontSize: 12, color: 'orange' }} >
                                            Same as KPI
                                        </Typography>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid item xs={12} sx={{ pt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }} >
                                {record.difOpp == "less" &&
                                    <Tooltip title={'Downward trend'} >
                                        <TrendingDownTwoToneIcon sx={{ color: 'red' }} />
                                    </Tooltip>
                                }
                                {record.difOpp == "more" &&
                                    <Tooltip title={'Upward trend'} >
                                        <TrendingUpTwoToneIcon sx={{ color: 'green' }} />
                                    </Tooltip>
                                }
                                {record.difOpp == "same" &&
                                    <Tooltip title={'No trend change'} >
                                        <TrendingFlatTwoToneIcon sx={{ color: 'orange' }} />
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            </Grid>
        )
    }
    else {
        return <Box>
            <Card variant="outlined">
                <CardContent>
                    <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Card >
                            <Skeleton variant="text" sx={{ fontSize: 16, ml: 3, mr: 3 }} width={200} />
                        </Card>
                    </Grid>
                    <Grid container spacing={1} justifyContent={'left'}>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

        </Box>
    }

}

export { DashboardKPIDetails }
export default DashboardKPIDetails
